import { Form, Input, Radio, Select } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { fuelApi } from '../../../../../apis'
import { ActionEnum, AlertMessageEnum } from '../../../../../common'
import {
  ManageModalComponent,
  ManageModalFooterComponent,
  NumberFormat,
} from '../../../../../components'
import { translate } from '../../../../../languages'
import {
  FuelEntity,
  FuelTypeEnum,
  FuelUnitEnum,
  UserTypeEnum,
} from '../../../../../models'
import { RootState } from '../../../../../stores'
import {
  loadProfile,
  loadPumps,
  loadTanks,
  setAction,
  setActionProcessing,
  setAlert,
  setFuels,
  setSelectedFuel,
} from '../../../../../stores/actions'

const StyledFormItem = styled(Form.Item)`
  margin-top: 5px;
`

const StyledLabel = styled.span`
  font-weight: bold;
`

type InputFuel = {
  name: string
  unit: FuelUnitEnum
  price: number
  type: FuelTypeEnum
}

export const ManageFuelModalComponent: React.FC = () => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { selectedFuel, fuels } = useSelector((state: RootState) => state.fuel)
  const { profile } = useSelector((state: RootState) => state.auth)
  const { language } = useSelector((state: RootState) => state.language)
  const { isTanksLoaded } = useSelector((state: RootState) => state.tank)
  const { isPumpsLoaded } = useSelector((state: RootState) => state.pump)
  const { management } = useSelector((state: RootState) => state.common)
  const readOnlyMode = React.useMemo(
    () => [ActionEnum.VIEW, ActionEnum.DELETE].includes(management.action),
    [management]
  )

  const replaceFuel = (fuel: FuelEntity) => {
    const index = fuels.map((item) => item.id).indexOf(fuel.id)
    if (index > -1) {
      dispatch(
        setFuels([...fuels.slice(0, index), fuel, ...fuels.slice(index + 1)])
      )
    }
  }

  const onFinish = async (value: InputFuel) => {
    dispatch(setAlert())
    dispatch(setActionProcessing(true))
    switch (management.action) {
      case ActionEnum.VIEW:
        dispatch(setAction(ActionEnum.UPDATE))
        break
      case ActionEnum.UPDATE:
        if (selectedFuel) {
          try {
            const respUpdate = await fuelApi.updateFuel(selectedFuel.id, value)
            replaceFuel(respUpdate.data)
            if (selectedFuel.name !== value.name) {
              if (isTanksLoaded) {
                dispatch(loadTanks())
              }
              if (isPumpsLoaded) {
                dispatch(loadPumps())
              }
            }
            dispatch(setSelectedFuel())
            dispatch(
              setAlert({
                key: 'fuel.update.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } catch (e) {
            if (e && e.response && e.response.status === 401) {
              dispatch(loadProfile())
              const respUpdate = await fuelApi.updateFuel(
                selectedFuel.id,
                value
              )
              replaceFuel(respUpdate.data)
              if (selectedFuel.name !== value.name) {
                if (isTanksLoaded) {
                  dispatch(loadTanks())
                }
                if (isPumpsLoaded) {
                  dispatch(loadPumps())
                }
              }
              dispatch(setSelectedFuel())
              dispatch(
                setAlert({
                  key: 'fuel.update.success',
                  type: AlertMessageEnum.SUCCESS,
                })
              )
            } else if (e && e.response && e.response.status === 409) {
              dispatch(
                setAlert({
                  key: 'fuel.update.fail.duplicate.name',
                  type: AlertMessageEnum.ERROR,
                })
              )
            } else {
              dispatch(
                setAlert({
                  key: 'fuel.update.fail.internal',
                  type: AlertMessageEnum.ERROR,
                })
              )
            }
          }
        }
        break
      case ActionEnum.CREATE:
        try {
          const respCreate = await fuelApi.createFuel(value)
          dispatch(setFuels([respCreate.data, ...fuels]))
          dispatch(setSelectedFuel())
          dispatch(
            setAlert({
              key: 'fuel.create.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e && e.response && e.response.status === 401) {
            dispatch(loadProfile())
            const respCreate = await fuelApi.createFuel(value)
            dispatch(setFuels([respCreate.data, ...fuels]))
            dispatch(setSelectedFuel())
            dispatch(
              setAlert({
                key: 'fuel.create.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else if (e && e.response && e.response.status === 409) {
            dispatch(
              setAlert({
                key: 'fuel.create.fail.duplicate.name',
                type: AlertMessageEnum.ERROR,
              })
            )
          } else {
            dispatch(
              setAlert({
                key: 'fuel.create.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      case ActionEnum.DELETE:
        try {
          await fuelApi.deleteFuel(selectedFuel?.id || 0)
          dispatch(
            setFuels(fuels.filter((item) => item.id !== selectedFuel?.id))
          )
          dispatch(setSelectedFuel())
          dispatch(
            setAlert({
              key: 'fuel.delete.success',
              type: AlertMessageEnum.SUCCESS,
            })
          )
        } catch (e) {
          if (e && e.response && e.response.status === 401) {
            dispatch(loadProfile())
            await fuelApi.deleteFuel(selectedFuel?.id || 0)
            dispatch(
              setFuels(fuels.filter((item) => item.id !== selectedFuel?.id))
            )
            dispatch(setSelectedFuel())
            dispatch(
              setAlert({
                key: 'fuel.delete.success',
                type: AlertMessageEnum.SUCCESS,
              })
            )
          } else {
            dispatch(
              setAlert({
                key:
                  e.response && e.response.status && e.response.status === 400
                    ? 'common.delete.fail.fkConstraint'
                    : 'fuel.delete.fail.internal',
                type: AlertMessageEnum.ERROR,
              })
            )
          }
        }
        break
      default:
        break
    }
    dispatch(setActionProcessing(false))
  }

  const body = (
    <Form onFinish={onFinish} form={form}>
      {(!management.alertMessage ||
        management.alertMessage.type !== AlertMessageEnum.SUCCESS) && (
        <>
          {readOnlyMode && selectedFuel && (
            <>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('fuel.name', language)}</StyledLabel>
                }
              >
                {selectedFuel.name}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('fuel.unit', language)}</StyledLabel>
                }
              >
                {translate(
                  `fuel.unit.${selectedFuel.unit.toLowerCase()}`,
                  language
                )}
              </StyledFormItem>
              <StyledFormItem
                label={
                  <StyledLabel>{translate('fuel.price', language)}</StyledLabel>
                }
              >
                <NumberFormat
                  value={selectedFuel.price}
                  suffixKey="common.moneyType"
                />
              </StyledFormItem>
            </>
          )}
          {!readOnlyMode && (
            <>
              <StyledFormItem
                name="name"
                label={
                  <StyledLabel>{translate('fuel.name', language)}</StyledLabel>
                }
                initialValue={selectedFuel?.name}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || !value.trim().length) {
                        return Promise.reject(
                          new Error(translate('fuel.invalid.name', language))
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  placeholder={translate('fuel.name', language)}
                  allowClear
                />
              </StyledFormItem>
              <StyledFormItem
                name="unit"
                label={
                  <StyledLabel>{translate('fuel.unit', language)}</StyledLabel>
                }
                initialValue={selectedFuel?.unit}
                rules={[
                  {
                    required: true,
                    message: translate('fuel.required.unit', language),
                  },
                ]}
              >
                <Select
                  style={{ width: '100%' }}
                  placeholder={translate('fuel.unit', language)}
                >
                  {Object.values(FuelUnitEnum).map((item) => (
                    <Select.Option value={item} key={item}>
                      {translate(`fuel.unit.${item.toLowerCase()}`, language)}
                    </Select.Option>
                  ))}
                </Select>
              </StyledFormItem>
              <StyledFormItem
                name="price"
                label={
                  <StyledLabel>{translate('fuel.price', language)}</StyledLabel>
                }
                initialValue={selectedFuel?.price}
                rules={[
                  {
                    required: true,
                    message: '',
                  },
                  () => ({
                    validator(_, value) {
                      if (!value || value < 1) {
                        return Promise.reject(
                          new Error(translate('fuel.invalid.price', language))
                        )
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
              >
                <Input
                  type="number"
                  placeholder={translate('fuel.price', language)}
                  allowClear
                />
              </StyledFormItem>
            </>
          )}
          {(readOnlyMode || management.action === ActionEnum.UPDATE) &&
            selectedFuel && (
              <StyledFormItem
                label={
                  <StyledLabel>{translate('fuel.type', language)}</StyledLabel>
                }
              >
                {translate(
                  `fuel.type.${selectedFuel.type.toLowerCase()}`,
                  language
                )}
              </StyledFormItem>
            )}
          {management.action === ActionEnum.CREATE && (
            <StyledFormItem
              name="type"
              label={
                <StyledLabel>{translate('fuel.type', language)}</StyledLabel>
              }
              initialValue={selectedFuel?.type}
              rules={[
                {
                  required: true,
                  message: translate('fuel.required.type', language),
                },
              ]}
            >
              <Radio.Group>
                {Object.values(FuelTypeEnum).map((item) => {
                  return (
                    <Radio value={item} key={item}>
                      {translate(`fuel.type.${item.toLowerCase()}`, language)}
                    </Radio>
                  )
                })}
              </Radio.Group>
            </StyledFormItem>
          )}
        </>
      )}
      <ManageModalFooterComponent
        hasDefaultButtons={
          profile &&
          profile.userType.type === UserTypeEnum.ADMIN &&
          (!management.alertMessage ||
            management.alertMessage.type !== AlertMessageEnum.SUCCESS)
            ? true
            : false
        }
        onDeleteConfirm={() => form.submit()}
      />
    </Form>
  )

  return <ManageModalComponent body={body} />
}
